import React from "react";
import { Section } from "../../../components/section";
import { SectionHeader } from "../../../components/section-header";
import { ParagraphGroup } from "../../../components/paragraph-group";
import { FullWidthImage } from "../../../components/full-width-image";
import { MethodOverview } from "../../../components/method-overview";
import { ScreenGroup } from "../../../components/screen-group";
import { SolutionHeader } from "../../../components/solution-header";
import { ImageTextTriplets } from "../../../components/image-text-triplets";
import sketches from "../../../images/sketches.png";
import experts from "../../../images/expert-interviews.png";
import spreadsheet from "../../../images/spreadsheet.png";
import corrections from "../../../images/corrections-s1.png";
import factchecker from "../../../images/fact-checker-s1.png";
import customization from "../../../images/feed-customization-s1.png";
import credibility from "../../../images/credibility-s1.png";

export interface IIdeationProps {
    sectionId: string;
    index: number;
}

export function Ideation(props: IIdeationProps) {
    const formattedIndex = props.index > 10 ? props.index : `0${props.index}`;
    return (
        <Section
            sectionId={props.sectionId}
            isLast={false}
        >
            <SectionHeader
                title={`${formattedIndex} Ideation`}
            />
            <ParagraphGroup
                body={[
                    "We began designing our solution by sketching potential features that could address the core user goals of quick evaluation and self-reflection."
                ]}
            />
            <FullWidthImage
                imgSrc={sketches}
            />
            <ParagraphGroup
                body={
                    [
                        "After reviewing the ideas, we tested with users and began to narrow down into a cohesive solution."
                    ]
                }
            />
            <MethodOverview
                methods={
                    [
                        {
                            title: "Think-Aloud Testing",
                            body: "We identified which sketches we thought had the most potential for meeting our user goals, and created 6 different low-fidelity prototypes to test with users.",
                            imgSrc: experts
                        },
                        {
                            title: "Narrowing Framework",
                            body: "We synthesized our findings from user testing and created a quantitative narrowing framework to balance competing factors such as effectiveness against misinformation, likelihood of user adoption, and technical feasibility.",
                            imgSrc: spreadsheet
                        }
                    ]
                }
            />
            <ScreenGroup
                title="What worked"
                description="Through this process, we determined which features had the most potential to be effective in helping users combat misinformation, and whether users actually wanted these features."
                features={
                    [
                        {
                            title: "Corrections",
                            body: "Provides updates on content users had engaged with previously to keep users up-to-date on the latest facts",
                            imgSrc: corrections
                        },
                        {
                            title: "Fact-Checker",
                            body: "Indicates the credibility of content in-feed both before and after posting to help users discern between fact and fiction",
                            imgSrc: factchecker
                        },
                        {
                            title: "Feed Customization",
                            body: "Allows user to customize their feed by topic, credibility, and content diversity to increase awareness of their consumption and promote exploration of new topics and viewpoints",
                            imgSrc: customization
                        },
                        {
                            title: "Activity Report",
                            body: "Shows users trends in their engagement over time, including credibility, and includes settings to adjust recommendation algorithms and block accounts that spread misinformation",
                            imgSrc: credibility
                        }
                    ]
                }
            />
            <ParagraphGroup
                title="what we learned"
                body={
                    [
                        "Though our participants saw potential in these features, they needed more explainability, especially in how credibility was evaluated with the AI-powered fact-checker. In addition, participants felt that the Activity Report lacked actionability. We also gained a better understanding of the desired level of customization."
                    ]
                }
            />
            <SolutionHeader
                title="what didn't work"
            />
            <ImageTextTriplets
                children={
                    [
                        {
                            title: "Quiz",
                            body: "Participants simply did not like quizzes, and felt that they would not have the motivation to engage with this on a regular basis."
                        },
                        {
                            title: "Throttling",
                            body: "Participants were uncomfortable with the idea of content being censored or voices being silenced by the platform, even if the content contained misinformation."
                        },
                        {
                            title: "In-feed Visualization",
                            body: "Participants had trouble understanding the visualization, and felt like they were already aware of the information it was providing."
                        }
                    ]
                }
            />
        </Section>
    );
}
